<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <!-- Photo confirmation screen with retake/accept options -->
  <teleport to="body">
    <div
      class="fixed inset-0 z-100000 bg-white dark:bg-black/95 flex flex-col overflow-hidden">
      <!-- Header -->
      <div
        class="absolute top-0 left-0 right-0 flex justify-center items-center p-4 z-50">
        <!-- Close Button -->
        <button
          @click="$emit('close')"
          class="absolute left-4 top-4 camera-btn camera-btn--medium camera-btn--close">
          <X class="w-6 h-6" />
        </button>

        <!-- Title -->
        <div
          class="px-4 py-2 bg-black/50 dark:bg-black/50 rounded-2xl text-white text-2xl font-semibold">
          {{ $t('views.camera.confirm.title') }}
        </div>
      </div>

      <!-- Photo Preview -->
      <div
        class="relative flex-1 overflow-hidden bg-black flex items-center justify-center">
        <img
          :src="photoData"
          class="w-full h-full object-contain transition-opacity duration-150"
          :class="{ 'opacity-0': !isLoaded }"
          @load="isLoaded = true"
          alt="Captured photo" />

        <!-- Action Buttons -->
        <div class="absolute bottom-6 left-0 right-0 px-4">
          <div class="flex justify-center gap-24">
            <!-- Retake Button -->
            <button
              @click="emit('retake')"
              class="camera-btn camera-btn--medium camera-btn--close flex items-center gap-2">
              <RotateCcw class="w-6 h-6" />
              <span class="hidden sm:inline">{{
                $t('views.camera.confirm.retake')
              }}</span>
            </button>

            <!-- Accept Button -->
            <button
              @click="emit('accept')"
              class="camera-btn camera-btn--medium camera-btn--accept flex items-center gap-2">
              <Check class="w-6 h-6" />
              <span class="hidden sm:inline">{{
                $t('views.camera.confirm.accept')
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup>
  import { ref } from 'vue'
  import { RotateCcw, Check, X } from 'lucide-vue-next'

  const props = defineProps({
    photoData: {
      type: String,
      required: true,
    },
  })

  const emit = defineEmits(['retake', 'accept', 'close'])

  const isLoaded = ref(false)
</script>
